import {
  ActionIcon,
  Badge,
  Group,
  MultiSelect,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconBookmark,
  IconExclamationMark,
  IconSearch,
  IconStar,
  IconStarFilled,
} from "@tabler/icons-react";
import {
  TableActions,
  EditAction,
  DeleteAction,
  actionsColumn,
} from "../TableActions";
import dayjs from "dayjs";

import { DataTable } from "../DataTable";
import { useDeleteSession } from "../../api/session";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import { displayDateTime } from "./utils/displayDateTime";
import { useGetTagColors } from "../../api/tag";
import { getTagColor } from "../Tags/utils/getTagColor";
import ExpandedRow from "./ExpandedRow";
import { useGetEvent } from "../../api/event";
import { useIsFeatureEnabled } from "../../api/featureFlags";
import { FEATURE_FLAGS } from "@congrs/schemas";

export default function SessionsTable({
  data,
  isLoading,
  enableActions,
  page,
  setPage,
  sortStatus,
  setSort,
  eventId,
  location,
  changeLocation,
  eventLocations,
}) {
  const { mutate } = useDeleteSession();
  const confirmDelete = useDeleteConfirm("session", mutate);
  const { data: tagColorsData } = useGetTagColors(eventId);
  const { data: event } = useGetEvent(eventId);
  const isRatingsEnabled = useIsFeatureEnabled(
    eventId,
    FEATURE_FLAGS.SESSION_RATINGS
  );

  const eventDates = event?.dates?.map((date) => date.date);

  return (
    <DataTable
      records={data?.data}
      columns={[
        {
          accessor: "title",
          title: "Title",
          sortable: true,
          render: (session) => (
            <>
              <Text size="sm">{session.title}</Text>
              {session.canceledAt !== null && (
                <Badge color="red" variant="light" size="sm">
                  Canceled
                </Badge>
              )}
            </>
          ),
        },
        {
          accessor: "starting",
          sortable: true,
          render: (session) => renderSessionDate(session.starting, eventDates),
        },
        {
          accessor: "ending",
          sortable: true,
          render: (session) => renderSessionDate(session.ending, eventDates),
        },
        {
          accessor: "location",
          sortable: true,
          filter: (
            <MultiSelect
              label="Locations"
              placeholder="Search locations…"
              value={location}
              data={eventLocations}
              onChange={(value) => {
                changeLocation(value);
              }}
              leftSection={<IconSearch size={16} />}
              comboboxProps={{ withinPortal: false }}
              clearable
              searchable
            />
          ),
          render: (row) => row.location?.name,
          filtering: location.length > 0,
        },
        {
          accessor: "sponsor",
          title: "Sponsor",
          sortable: true,
          render: (session) =>
            session.sponsor?.id && (
              <Text size="sm">{session.sponsor.name}</Text>
            ),
        },
        {
          accessor: "speakers",
          sortable: true,
          render: (session) =>
            session?.speakers?.map((speaker, index) => {
              return (
                <Group key={`${speaker?.firstName}-${speaker?.lastName}`}>
                  <Text size="sm">
                    {speaker?.occupation
                      ? `${speaker?.firstName} ${speaker?.lastName}, ${speaker?.occupation}`
                      : `${speaker?.firstName} ${speaker?.lastName}`}
                  </Text>
                </Group>
              );
            }),
        },
        {
          accessor: "tags",
          title: "Tags",
          sortable: true,
          render: (session) =>
            [...(session.targetedTags || []), ...(session.tags || [])].map(
              (tag) => (
                <Badge
                  key={tag.id}
                  color={getTagColor(tagColorsData, tag.subtype)?.color}
                  variant="light"
                  size="sm"
                  m={1}
                >
                  {tag.title}
                </Badge>
              )
            ),
        },
        {
          accessor: "bookmarks",
          title: <IconBookmark />,
          textAlign: "center",
          sortable: true,
          render: (session) =>
            session.bookmarks !== 0 ? session.bookmarks : "",
        },
        ...(isRatingsEnabled
          ? [
              {
                accessor: "rating",
                title: <IconStar />,
                textAlign: "center",
                sortable: true,
                render: (session) => (
                  <Group gap={3}>
                    {session.rating}
                    <IconStarFilled color="orange" size={16} />
                  </Group>
                ),
              },
            ]
          : []),
        actionsColumn((session) => (
          <TableActions>
            <EditAction to={`${session.id}`} />
            <DeleteAction
              onDelete={() => confirmDelete({ id: session.id })}
              disabled={!enableActions}
            />
          </TableActions>
        )),
      ]}
      rowExpansion={{
        content: ({ record }) => (
          <ExpandedRow
            description={record.description}
            descriptionRich={record.descriptionRich}
          />
        ),
      }}
      page={page}
      onPageChange={setPage}
      recordsPerPage={data?.pagination.perPage}
      totalRecords={data?.pagination.total}
      fetching={isLoading}
      sortStatus={sortStatus}
      onSortStatusChange={setSort}
    />
  );
}

const WarningIcon = () => (
  <Tooltip label="Session date is not in the event dates. Please update the session.">
    <ActionIcon variant="filled" color="red" radius="xl" size="xs">
      <IconExclamationMark />
    </ActionIcon>
  </Tooltip>
);

const renderSessionDate = (date, eventDates) => {
  // Check if the session date is in the event dates
  // If not, display the session date
  // If it is, display the event date

  const sessionDate = dayjs(date).format("YYYY-MM-DD");
  const dates = eventDates?.find((date) => date === sessionDate);

  const isInRange = dates?.includes(sessionDate);

  return (
    <Group wrap="nowrap">
      <Text size="sm">{displayDateTime(date)}</Text>
      {!isInRange && <WarningIcon />}
    </Group>
  );
};
